import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Button, Form, Spinner } from 'react-bootstrap';
import {
  getBrandSettings,
  resetBrandSettings,
  updateBrandSettings,
  uploadBrandAssets,
} from '../services/api';
import { setAlert } from '../store/slices/alertSlice';
import { DEFAULT_BRAND, resolveBrandAssetUrl, useBrand } from '../context/BrandContext';

const emptyForm = { ...DEFAULT_BRAND };

const BrandSettings = () => {
  const dispatch = useDispatch();
  const { setBrandState } = useBrand();
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getBrandSettings();
      setFormData({ ...DEFAULT_BRAND, ...data });
      setBrandState(data);
    } catch (error) {
      dispatch(setAlert({ type: 'danger', message: 'Error loading brand settings' }));
    } finally {
      setLoading(false);
    }
  }, [dispatch, setBrandState]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({ ...current, [name]: value }));
  };

  const handleSave = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        orgName: formData.orgName.trim(),
        orgShortName: formData.orgShortName.trim(),
        productName: formData.productName.trim(),
        productShortName: formData.productShortName.trim(),
        tagline: formData.tagline.trim(),
        taglineShort: formData.taglineShort.trim(),
        refNoPrefix: formData.refNoPrefix.trim(),
        primaryColor: formData.primaryColor.trim(),
        primaryColorDark: formData.primaryColorDark.trim(),
        accentColor: formData.accentColor.trim(),
      };
      const { data } = await updateBrandSettings(payload);
      dispatch(setAlert({ type: 'success', message: data.message || 'Brand settings saved' }));
      setFormData({ ...DEFAULT_BRAND, ...data.settings });
      setBrandState(data.settings);
    } catch (error) {
      dispatch(setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Failed to save brand settings',
      }));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Reset branding to Royal Medical Center defaults? Uploaded images paths will clear.')) {
      return;
    }
    setResetting(true);
    try {
      const { data } = await resetBrandSettings();
      dispatch(setAlert({ type: 'success', message: data.message || 'Brand settings reset' }));
      setFormData({ ...DEFAULT_BRAND, ...data.settings });
      setBrandState(data.settings);
    } catch (error) {
      dispatch(setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Failed to reset brand settings',
      }));
    } finally {
      setResetting(false);
    }
  };

  const handleAssetChange = async (fieldName, file) => {
    if (!file) return;
    const body = new FormData();
    body.append(fieldName, file);
    setUploading(true);
    try {
      const { data } = await uploadBrandAssets(body);
      dispatch(setAlert({ type: 'success', message: data.message || 'Asset uploaded' }));
      setFormData({ ...DEFAULT_BRAND, ...data.settings });
      setBrandState(data.settings);
    } catch (error) {
      dispatch(setAlert({
        type: 'danger',
        message: error.response?.data?.error || 'Failed to upload asset',
      }));
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-page d-flex justify-content-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }

  const preview = {
    logo: resolveBrandAssetUrl(formData.logoUrl, '/logo.png'),
    mark: resolveBrandAssetUrl(formData.markUrl, '/rmc-mark.png'),
    hero: resolveBrandAssetUrl(formData.heroUrl, '/irs-hero.png'),
    favicon: resolveBrandAssetUrl(formData.faviconUrl, '/favicon.ico'),
  };

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <h3>Brand Settings</h3>
          <p>
            Configure organization name, logos, colors, and incident reference prefix for this deployment
            (e.g. RMC now, SMC on another host).
          </p>
        </div>
      </div>

      <Form onSubmit={handleSave} className="card p-3 shadow-sm">
        <div className="row g-3">
          <div className="col-md-6">
            <Form.Group controlId="brand-org-name">
              <Form.Label>Organization Name</Form.Label>
              <Form.Control name="orgName" value={formData.orgName} onChange={handleChange} />
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group controlId="brand-org-short">
              <Form.Label>Organization Short Name</Form.Label>
              <Form.Control name="orgShortName" value={formData.orgShortName} onChange={handleChange} placeholder="RMC / SMC" />
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group controlId="brand-product-name">
              <Form.Label>Product Name</Form.Label>
              <Form.Control name="productName" value={formData.productName} onChange={handleChange} />
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group controlId="brand-product-short">
              <Form.Label>Product Short Name</Form.Label>
              <Form.Control name="productShortName" value={formData.productShortName} onChange={handleChange} placeholder="IRS" />
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group controlId="brand-tagline">
              <Form.Label>Landing Tagline</Form.Label>
              <Form.Control name="tagline" value={formData.tagline || ''} onChange={handleChange} placeholder="Occurrence Variance Reporting" />
            </Form.Group>
          </div>
          <div className="col-md-6">
            <Form.Group controlId="brand-tagline-short">
              <Form.Label>Short Tagline</Form.Label>
              <Form.Control name="taglineShort" value={formData.taglineShort || ''} onChange={handleChange} placeholder="OVR registry" />
            </Form.Group>
          </div>
          <div className="col-12">
            <Form.Group controlId="brand-ref-prefix">
              <Form.Label>Incident Ref Prefix</Form.Label>
              <Form.Control name="refNoPrefix" value={formData.refNoPrefix} onChange={handleChange} placeholder="AZG/RMC/OVR/" />
              <Form.Text muted>New incidents only. Existing ref numbers are not changed.</Form.Text>
            </Form.Group>
          </div>
          <div className="col-md-4">
            <Form.Group controlId="brand-primary">
              <Form.Label>Primary Color</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control type="color" name="primaryColor" value={formData.primaryColor || '#9f1733'} onChange={handleChange} />
                <Form.Control name="primaryColor" value={formData.primaryColor} onChange={handleChange} />
              </div>
            </Form.Group>
          </div>
          <div className="col-md-4">
            <Form.Group controlId="brand-primary-dark">
              <Form.Label>Primary Dark</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control type="color" name="primaryColorDark" value={formData.primaryColorDark || '#7f1028'} onChange={handleChange} />
                <Form.Control name="primaryColorDark" value={formData.primaryColorDark} onChange={handleChange} />
              </div>
            </Form.Group>
          </div>
          <div className="col-md-4">
            <Form.Group controlId="brand-accent">
              <Form.Label>Accent Color</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control type="color" name="accentColor" value={formData.accentColor || '#c79a4b'} onChange={handleChange} />
                <Form.Control name="accentColor" value={formData.accentColor} onChange={handleChange} />
              </div>
            </Form.Group>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 mt-4">
          <Button type="submit" variant="primary" disabled={saving || uploading}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
          <Button type="button" variant="outline-secondary" onClick={handleReset} disabled={resetting || uploading}>
            {resetting ? 'Resetting...' : 'Reset to Defaults'}
          </Button>
        </div>
      </Form>

      <div className="card p-3 shadow-sm mt-3">
        <h5 className="mb-3">Brand Assets</h5>
        <div className="row g-3">
          {[
            { key: 'logo', label: 'Logo', preview: preview.logo },
            { key: 'mark', label: 'Mark / Icon', preview: preview.mark },
            { key: 'hero', label: 'Hero Background', preview: preview.hero },
            { key: 'favicon', label: 'Favicon', preview: preview.favicon },
          ].map((asset) => (
            <div className="col-md-6" key={asset.key}>
              <Form.Group>
                <Form.Label>{asset.label}</Form.Label>
                <div className="d-flex align-items-center gap-3 mb-2">
                  <img
                    src={asset.preview}
                    alt={asset.label}
                    style={{
                      width: asset.key === 'hero' ? 120 : 56,
                      height: asset.key === 'hero' ? 64 : 56,
                      objectFit: 'contain',
                      background: '#f8f5f0',
                      border: '1px solid #e7dfd7',
                      borderRadius: 8,
                    }}
                  />
                  <Form.Control
                    type="file"
                    accept="image/*,.ico"
                    disabled={uploading}
                    onChange={(event) => handleAssetChange(asset.key, event.target.files?.[0])}
                  />
                </div>
              </Form.Group>
            </div>
          ))}
        </div>
        {uploading && <p className="text-muted small mb-0 mt-2">Uploading...</p>}
      </div>
    </div>
  );
};

export default BrandSettings;
